import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth/AuthContext';
import { ActivityLog } from '@/types/supabase';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { 
  Loader2, AlertCircle, FileText, Users, Building2, 
  ChartBar, Database, LogIn, Flag, Download, Search, Filter,
  Calendar, X, Check, RefreshCw
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { 
  Table, TableHeader, TableBody, TableHead, 
  TableRow, TableCell, TableCaption 
} from '@/components/ui/table';
import { 
  Pagination, PaginationContent, PaginationItem, 
  PaginationLink, PaginationNext, PaginationPrevious 
} from '@/components/ui/pagination';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useActivityLogger } from '@/hooks/use-activity-logger';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// Add relative time plugin to dayjs
dayjs.extend(relativeTime);

// Activity type definitions
type ActivityType = 'system' | 'grievance' | 'user' | 'user_organizations' | 
                   'department' | 'analytics' | 'document' | 'project' | 'organization';

interface ActivityTypeConfig {
  label: string;
  badgeClass: string;
  icon: React.ElementType;
}

// Define activity type configurations
const ACTIVITY_TYPES: Record<ActivityType, ActivityTypeConfig> = {
  system: { 
    label: 'System', 
    badgeClass: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
    icon: LogIn
  },
  grievance: { 
    label: 'Grievance', 
    badgeClass: 'bg-red-100 text-red-700 hover:bg-red-200',
    icon: Flag
  },
  user: { 
    label: 'User', 
    badgeClass: 'bg-green-100 text-green-700 hover:bg-green-200',
    icon: Users
  },
  user_organizations: { 
    label: 'Team', 
    badgeClass: 'bg-green-100 text-green-700 hover:bg-green-200',
    icon: Users
  },
  department: { 
    label: 'Department', 
    badgeClass: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
    icon: Building2
  },
  analytics: { 
    label: 'Analytics', 
    badgeClass: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
    icon: ChartBar
  },
  document: { 
    label: 'Document', 
    badgeClass: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200',
    icon: FileText
  },
  project: { 
    label: 'Project', 
    badgeClass: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200',
    icon: Database
  },
  organization: { 
    label: 'Organization', 
    badgeClass: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200',
    icon: Building2
  }
};

interface ActivityLogsFullViewProps {
  onClose: () => void;
}

export function ActivityLogsFullView({ onClose }: ActivityLogsFullViewProps) {
  // State for activity logs and loading
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Authentication context
  const { currentOrganization } = useAuth();
  const { logActivity } = useActivityLogger();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Default filter dates - last 30 days
  const defaultStartDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
  const defaultEndDate = dayjs().format('YYYY-MM-DD');
  
  // Filter state
  const [filters, setFilters] = useState({
    resourceType: '',
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    userId: '',
    searchTerm: '',
    action: ''
  });

  // Activity actions for filtering
  const activityActions = useMemo(() => [
    { value: '', label: 'All Actions' },
    { value: 'view', label: 'View' },
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
    { value: 'access', label: 'Access' }
  ], []);
  
  // Fetch logs with pagination and filters
  const fetchActivityLogs = useCallback(async (isRefresh = false) => {
    if (!currentOrganization) return;

    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      // Start with the basic query
      let query = supabase
        .from('activity_logs')
        .select('*', { count: 'exact' })
        .eq('organization_id', currentOrganization.organization.id);

      // Apply filters
      if (filters.resourceType) {
        query = query.eq('resource_type', filters.resourceType);
      }

      if (filters.userId) {
        query = query.eq('user_id', filters.userId);
      }
      
      if (filters.action) {
        query = query.eq('action', filters.action);
      }
      
      if (filters.searchTerm) {
        query = query.or(`resource_type.ilike.%${filters.searchTerm}%,action.ilike.%${filters.searchTerm}%`);
      }

      // Apply date range filter
      const startDate = dayjs(filters.startDate).startOf('day').toISOString();
      const endDate = dayjs(filters.endDate).endOf('day').toISOString();
      
      query = query.gte('created_at', startDate).lte('created_at', endDate);

      // Order and paginate
      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * pageSize, currentPage * pageSize - 1);

      if (error) throw error;

      if (count !== null) {
        setTotalPages(Math.ceil(count / pageSize));
      }

      // Process user information for display
      const processedLogs = data.map(log => ({
        ...log,
        user: log.user_id ? {
          id: log.user_id,
          email: `${log.user_id.split('-')[0]}@example.com`
        } : undefined
      }));

      setActivityLogs(processedLogs);
      
      // Log the activity if this is the first load
      if (!isRefresh && currentPage === 1) {
        logActivity({
          action: 'view',
          resourceType: 'system',
          details: { component: 'ActivityLogsFullView' }
        });
      }
    } catch (err: any) {
      console.error('Error fetching activity logs:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [currentOrganization, currentPage, pageSize, filters, logActivity]);

  // Initial fetch and refetch on filter/pagination changes
  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs, currentPage, pageSize, filters]);

  // Handle filtering
  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    setFilters({
      resourceType: '',
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      userId: '',
      searchTerm: '',
      action: ''
    });
    setCurrentPage(1);
  }, [defaultStartDate, defaultEndDate]);

  // Handle manual refresh
  const handleRefresh = useCallback(() => {
    fetchActivityLogs(true);
  }, [fetchActivityLogs]);

  // Handle export
  const handleExport = useCallback((format: 'csv' | 'excel' | 'pdf') => {
    if (format === 'csv') {
      const headers = ['Timestamp', 'User', 'Action', 'Resource Type', 'Details'];
      
      const csvContent = activityLogs.map(log => {
        return [
          dayjs(log.created_at).format('YYYY-MM-DD HH:mm:ss'),
          log.user?.email || 'System',
          log.action,
          log.resource_type,
          JSON.stringify(log.details || {})
        ].join(',');
      });
      
      const csvData = [headers.join(','), ...csvContent].join('\n');
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `activity_logs_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Log the export action
      logActivity({
        action: 'export',
        resourceType: 'system',
        details: { format: 'csv', component: 'ActivityLogsFullView' }
      });
    } else {
      // For Excel and PDF (would use external libraries in production)
      alert(`Export to ${format} would be implemented with appropriate libraries in a production app`);
    }
  }, [activityLogs, logActivity]);

  // Memoized activity type badge and icon functions
  const getActivityTypeBadge = useCallback((resourceType: string) => {
    const type = resourceType as ActivityType;
    const config = ACTIVITY_TYPES[type] || {
      label: resourceType,
      badgeClass: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      icon: Database
    };
    
    return <Badge className={config.badgeClass}>{config.label}</Badge>;
  }, []);

  const getActivityIcon = useCallback((resourceType: string) => {
    const type = resourceType as ActivityType;
    const Icon = ACTIVITY_TYPES[type]?.icon || Database;
    return <Icon className="h-4 w-4" />;
  }, []);

  // Format activity description
  const getActivityDescription = useCallback((log: ActivityLog) => {
    const actor = log.user?.email?.split('@')[0] || 'System';
    const action = log.action.toLowerCase();
    const resourceType = log.resource_type.replace('_', ' ');
    
    let description = `${actor} ${action}ed a ${resourceType}`;
    
    if (log.details) {
      if (log.resource_type === 'department' && log.details.name) {
        description += `: ${log.details.name}`;
      } else if (log.resource_type === 'project' && log.details.name) {
        description += `: ${log.details.name}`;
      } else if (log.resource_type === 'document' && log.details.title) {
        description += `: ${log.details.title}`;
      } else if (log.resource_type === 'grievance' && log.details.title) {
        description += `: ${log.details.title}`;
      }
    }
    
    return description;
  }, []);

  // Pagination UI helpers
  const paginationItems = useMemo(() => {
    const items = [];
    let startPage, endPage;
    
    if (totalPages <= 5) {
      startPage = 1;
      endPage = totalPages;
    } else if (currentPage <= 3) {
      startPage = 1;
      endPage = 5;
    } else if (currentPage >= totalPages - 2) {
      startPage = totalPages - 4;
      endPage = totalPages;
    } else {
      startPage = currentPage - 2;
      endPage = currentPage + 2;
    }
    
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink 
            isActive={currentPage === i}
            onClick={() => setCurrentPage(i)}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return items;
  }, [currentPage, totalPages]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 text-destructive p-8">
        <AlertCircle className="h-12 w-12" />
        <div className="text-center">
          <h3 className="text-lg font-medium mb-2">Failed to load activity logs</h3>
          <p>{error}</p>
          <Button 
            variant="outline" 
            className="mt-4" 
            onClick={() => fetchActivityLogs()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2">
      {/* Filter Controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative w-full md:w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search activities..."
            className="pl-8"
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
          />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Filters
              {Object.values(filters).some(v => v !== '' && 
                v !== defaultStartDate && v !== defaultEndDate) && (
                <Badge variant="secondary" className="ml-1 h-5 px-1">
                  <Check className="h-3 w-3" />
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">Filter Options</h4>
                <p className="text-sm text-muted-foreground">
                  Narrow down activities by these criteria
                </p>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="resource-type">Resource Type</Label>
                <select 
                  id="resource-type" 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  value={filters.resourceType}
                  onChange={(e) => handleFilterChange('resourceType', e.target.value)}
                >
                  <option value="">All Types</option>
                  {Object.entries(ACTIVITY_TYPES).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="action-type">Action</Label>
                <select 
                  id="action-type" 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                >
                  {activityActions.map(action => (
                    <option key={action.value} value={action.value}>{action.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="start-date">Start Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input 
                    id="start-date" 
                    type="date" 
                    className="pl-8"
                    value={filters.startDate} 
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="end-date">End Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input 
                    id="end-date" 
                    type="date"
                    className="pl-8"
                    value={filters.endDate} 
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
              </div>
              
              <Button 
                onClick={handleResetFilters}
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4" />
                Reset Filters
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        
        <Button 
          variant="outline" 
          className="gap-2" 
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>

        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Export Activity Logs</DialogTitle>
              <DialogDescription>
                Choose a format to export the current filtered view
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="flex items-center justify-center gap-4">
                <Button onClick={() => handleExport('csv')} className="gap-2">
                  <Download className="h-4 w-4" />
                  CSV
                </Button>
                <Button onClick={() => handleExport('excel')} className="gap-2">
                  <Download className="h-4 w-4" />
                  Excel
                </Button>
                <Button onClick={() => handleExport('pdf')} className="gap-2">
                  <Download className="h-4 w-4" />
                  PDF
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        <div className="ml-auto">
          <select
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing page size
            }}
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
        </div>
      </div>

      {/* Activity Log Table */}
      <Card className="shadow-sm">
        <Table>
          <TableCaption>A list of all activity logs for your organization.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Type</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Timestamp</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activityLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <Database className="h-8 w-8 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No activity logs found with the current filters.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              activityLogs.map((log) => (
                <TableRow key={log.id} className="group hover:bg-muted/50">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="p-1 rounded-full bg-gray-100">
                        {getActivityIcon(log.resource_type)}
                      </div>
                      {getActivityTypeBadge(log.resource_type)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="cursor-default">
                          {log.user?.email?.split('@')[0] || 'System'}
                        </TooltipTrigger>
                        <TooltipContent>
                          {log.user?.email || 'System Process'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell className="capitalize">
                    <Badge variant="outline">{log.action}</Badge>
                  </TableCell>
                  <TableCell>
                    {getActivityDescription(log)}
                  </TableCell>
                  <TableCell className="text-right">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="cursor-default">
                          {dayjs(log.created_at).fromNow()}
                        </TooltipTrigger>
                        <TooltipContent>
                          {dayjs(log.created_at).format('MMM D, YYYY h:mm A')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                aria-disabled={currentPage === 1}
                className={currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}
              />
            </PaginationItem>
            
            {paginationItems}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                aria-disabled={currentPage === totalPages}
                className={currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
